/**
 * PR76: Third-Party Tool Registry and License Boundary — Registry
 *
 * Metadata-only. No network call, no DB write, no secret literal,
 * no provider call, no file mutation at runtime, no command execution.
 * No third-party source code is copied into smart-shikka-core.
 *
 * Deep-frozen — every nested tool object, array, and notice is recursively
 * frozen so that runtime mutation of any entry, capability, blocked action,
 * or required notice is impossible.
 */
// ---------------------------------------------------------------------------
// Deep freeze helper
// ---------------------------------------------------------------------------
function deepFreeze(value) {
    if (value === null || typeof value !== "object")
        return value;
    Object.freeze(value);
    for (const key of Object.keys(value)) {
        const child = value[key];
        if (child !== null && typeof child === "object" && !Object.isFrozen(child)) {
            deepFreeze(child);
        }
    }
    return value;
}
// ---------------------------------------------------------------------------
// Third-party tools
// ---------------------------------------------------------------------------
export const THIRD_PARTY_TOOLS = deepFreeze([
    {
        id: "pake_desktop_builder",
        name: "Pake Desktop Builder",
        category: "desktop_builder",
        sourceRepo: "https://github.com/tw93/pake",
        uploadedArchiveName: "pake-source-archive.zip",
        licenseType: "GPL-3.0-or-later",
        licenseRisk: "critical",
        copyPolicy: "source_copy_prohibited",
        integrationMode: "external_build_tool",
        allowedCapabilities: [
            "desktop_window_packaging",
            "future_app_build_orchestration",
        ],
        blockedActions: [
            "source_code_copy",
            "binary_distribution_without_license_review",
            "runtime_library_integration",
            "network_call_from_registry",
            "auto_save",
            "auto_delete",
        ],
        requiredNotices: [
            "GPL-3.0-or-later license",
            "Pake source code will NOT be merged into smart-shikka-core",
            "External CLI/build tool only",
            "Original repository: https://github.com/tw93/pake",
        ],
        approvalRequired: true,
        liveExecutionAllowed: false,
        networkAllowed: false,
        productionDbAllowed: false,
        secretAccessAllowed: false,
        browserDesktopActionAllowed: false,
        fileMutationAllowed: false,
        status: "preview",
        nextPrRecommendedScope: "Desktop build adapter wiring for smart-print, smart-salary, drishti, digital-anoopam",
    },
    {
        id: "kami_document_renderer",
        name: "Kami Document Renderer",
        category: "document_renderer",
        sourceRepo: "https://github.com/kamicrafted/kami",
        uploadedArchiveName: "kami-source-archive.zip",
        licenseType: "MIT",
        licenseRisk: "low",
        copyPolicy: "copy_with_notice_required",
        integrationMode: "plan_only_skill_pattern",
        allowedCapabilities: [
            "document_template_preview",
            "report_layout_planning",
            "print_layout_preview",
        ],
        blockedActions: [
            "template_copy_without_notice",
            "script_copy_without_notice",
            "runtime_execution",
            "network_call_from_registry",
            "production_db_access",
        ],
        requiredNotices: [
            "MIT license",
            "Original repository attribution required",
            "Any copy or adaptation must include original repo and license",
            "Changelog notice if adapted",
        ],
        approvalRequired: true,
        liveExecutionAllowed: false,
        networkAllowed: false,
        productionDbAllowed: false,
        secretAccessAllowed: false,
        browserDesktopActionAllowed: false,
        fileMutationAllowed: false,
        status: "preview",
        nextPrRecommendedScope: "Document/report/print layout adapter for smart-print and smart-salary",
    },
    {
        id: "waza_skill_engine",
        name: "Waza Skill Engine",
        category: "skill_engine",
        sourceRepo: "https://github.com/waza-ee/waza",
        uploadedArchiveName: "waza-source-archive.zip",
        licenseType: "MIT",
        licenseRisk: "low",
        copyPolicy: "copy_with_notice_required",
        integrationMode: "plan_only_skill_pattern",
        allowedCapabilities: [
            "skill_pattern_preview",
            "think_check_hunt_read_health_planning",
        ],
        blockedActions: [
            "skill_code_copy_without_notice",
            "runtime_skill_execution",
            "network_call_from_registry",
            "production_db_access",
        ],
        requiredNotices: [
            "MIT license",
            "Original repository attribution required",
            "Plan-only skill pattern adaptation notice",
            "Changelog notice if adapted",
        ],
        approvalRequired: true,
        liveExecutionAllowed: false,
        networkAllowed: false,
        productionDbAllowed: false,
        secretAccessAllowed: false,
        browserDesktopActionAllowed: false,
        fileMutationAllowed: false,
        status: "preview",
        nextPrRecommendedScope: "Skill system integration through smart-shikka skill registry",
    },
    {
        id: "aiko_icon_system",
        name: "Aiko Icon System",
        category: "icon_system",
        sourceRepo: "https://github.com/aiko-ui/aiko",
        uploadedArchiveName: "aiko-source-archive.zip",
        licenseType: "MIT",
        licenseRisk: "high",
        copyPolicy: "attribution_required_before_copy",
        integrationMode: "registry_metadata_only",
        allowedCapabilities: [
            "icon_system_preview",
            "future_ai_interface_icon_mapping",
        ],
        blockedActions: [
            "icon_copy_without_attribution",
            "icon_copy_without_license_verification",
            "runtime_icon_mutation",
            "network_call_from_registry",
        ],
        requiredNotices: [
            "MIT license stated in README",
            "Separate LICENSE file not found — verification required before copy",
            "Attribution and license verification note required",
            "Original repository link must be preserved",
        ],
        approvalRequired: true,
        liveExecutionAllowed: false,
        networkAllowed: false,
        productionDbAllowed: false,
        secretAccessAllowed: false,
        browserDesktopActionAllowed: false,
        fileMutationAllowed: false,
        status: "preview",
        nextPrRecommendedScope: "AI interface icon system adapter with attribution gate",
    },
]);
// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------
export const SMART_SHIKKA_THIRD_PARTY_TOOL_REGISTRY = deepFreeze({
    tools: THIRD_PARTY_TOOLS,
    version: "1.0.0",
    generatedInPr: 76,
});
// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------
export function getThirdPartyToolById(id) {
    return THIRD_PARTY_TOOLS.find((t) => t.id === id);
}
export function getToolsByCategory(category) {
    return THIRD_PARTY_TOOLS.filter((t) => t.category === category);
}
export function getToolsRequiringLicenseNotice() {
    return THIRD_PARTY_TOOLS.filter((t) => t.copyPolicy === "copy_with_notice_required" ||
        t.copyPolicy === "attribution_required_before_copy");
}
export function getToolsByLicenseRisk(risk) {
    return THIRD_PARTY_TOOLS.filter((t) => t.licenseRisk === risk);
}
export function getBlockedActionsForTool(id) {
    return getThirdPartyToolById(id)?.blockedActions ?? [];
}
export function getAllThirdPartyToolIds() {
    return THIRD_PARTY_TOOLS.map((t) => t.id);
}
//# sourceMappingURL=thirdPartyToolRegistry.js.map